import { useMemo } from 'react';

import { ActionTile } from '@/components/layout/ActionTile/ActionTile';
import { Grid } from '@/components/layout/Grid';
import { DialogModal } from '@/components/modals/DialogModal/DialogModal';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { useIRSConstants } from '@/modules/irs/useIRSConstants';
import { DispositiveProvisionTemplateKind } from '@/types/schema';

import {
  DispositiveProvisionTemplateDetails,
  NEW_TEMPLATE_SENTINEL,
} from '../DispositiveProvisionsTemplateSplitScreenModal/DispositiveProvisionsTemplateSplitScreenModal.types';

interface TemplatePresetDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (details: DispositiveProvisionTemplateDetails) => void;
}

/**
 * TemplatePresetDialog presents the user with a selection of preset template
 * types as well as an option to start with no preset.  It mirrors the UI that
 * previously lived inline in `DispositiveProvisionTemplateMenu` so that the
 * behaviour can be reused across multiple entry points (e.g. the legacy DP
 * form and the new V2 form).
 */
export function TemplatePresetDialog({
  isOpen,
  onClose,
  onSelect,
}: TemplatePresetDialogProps) {
  const { isTwoClientHousehold, primaryClients } = useHouseholdDetailsContext();
  const { availableStateEstateTaxes } = useIRSConstants();

  const hasClientWithEstateTaxState = useMemo(() => {
    return primaryClients?.some(
      (client) =>
        client.stateCode &&
        availableStateEstateTaxes?.includes(client.stateCode)
    );
  }, [primaryClients, availableStateEstateTaxes]);

  const handleSelect = (templateType: DispositiveProvisionTemplateKind) => {
    onSelect({
      templateId: NEW_TEMPLATE_SENTINEL,
      templateType,
    });
    onClose();
  };

  return (
    <DialogModal
      heading="Create template from scratch or use a preset"
      isOpen={isOpen}
      onClose={onClose}
    >
      <Grid container spacing={2} flex={1} columns={12}>
        {isTwoClientHousehold && (
          <>
            <Grid item sm={6}>
              <ActionTile
                containerSx={{ height: '100%', width: '100%' }}
                variant="small"
                onClick={() =>
                  handleSelect(
                    DispositiveProvisionTemplateKind.OutrightToSurvivingSpouse
                  )
                }
                heading="Outright to surviving spouse"
                body="All assets distribute outright to the surviving spouse upon 1st grantor's death, with an option to disclaim a portion of the assets"
              />
            </Grid>
            <Grid item sm={6}>
              <ActionTile
                containerSx={{ height: '100%', width: '100%' }}
                variant="small"
                onClick={() =>
                  handleSelect(DispositiveProvisionTemplateKind.MaritalTrust)
                }
                heading="Marital trust"
                body="All assets distribute to a marital trust FBO surviving spouse upon 1st grantor's death. Option to disclaim or QTIP all or portion of the assets."
              />
            </Grid>
            <Grid item sm={6}>
              <ActionTile
                containerSx={{ height: '100%', width: '100%' }}
                variant="small"
                onClick={() =>
                  handleSelect(DispositiveProvisionTemplateKind.AbTrust)
                }
                heading="A/B trust"
                body="Establish combo of bypass & marital trusts upon death of 1st grantor, with an option to include a survivor's trust"
              />
            </Grid>
            {hasClientWithEstateTaxState && (
              <Grid item sm={6}>
                <ActionTile
                  containerSx={{ height: '100%', width: '100%' }}
                  variant="small"
                  onClick={() =>
                    handleSelect(DispositiveProvisionTemplateKind.AbcTrust)
                  }
                  heading="A/B/C trust"
                  body="Establish combo of bypass & marital trusts. Assumes state QTIP election is made on marital trust."
                />
              </Grid>
            )}
          </>
        )}
        <Grid item sm={6}>
          <ActionTile
            containerSx={{ height: '100%', width: '100%' }}
            variant="small"
            onClick={() =>
              handleSelect(DispositiveProvisionTemplateKind.PouroverWill)
            }
            heading="Pourover will"
            body="Assets not transferred into trust during the grantor's lifetime are transferred into a revocable trust upon the grantor's death"
          />
        </Grid>
        <Grid item sm={6}>
          <ActionTile
            containerSx={{ height: '100%', width: '100%' }}
            variant="small"
            onClick={() =>
              handleSelect(DispositiveProvisionTemplateKind.Default)
            }
            heading="No preset"
          />
        </Grid>
      </Grid>
    </DialogModal>
  );
}
